import { FloorTheme } from '@/lib/themes';
import { Button } from './ui/button';

interface FloorChangeModalProps {
    isOpen: boolean;
    onClose: () => void;
    theme: FloorTheme;
    fromFloor: number;
    toFloor: number;
    mode?: 'promotion' | 'demotion' | 'welcome';
}

const POINTS_GUIDE = [
    '校正が入らずにそのまま承認された投稿は +5 点です。',
    '校正が入った時点で -3 点が先に反映されます。',
    'AIの校正文を採用して投稿すると +5 点（結果としてその投稿は合計 +2 点）です。',
    '自分で書き直して承認された場合も、最初の -3 点は戻らないため合計 +2 点です。',
    'いいねを押すと +2 点、いいねを受け取ると +1 点です。',
    'コメントを送ると +3 点、コメントを受け取ると +2 点です（コメント時も、検閲NGの場合は -2 点されます）。',
    'スポットライト（Floor6より開放）：他の人の投稿を1件だけ優先表示できます。いつでも別の投稿に切り替え可能です。',
    'あなたがスポットライトをしている投稿に「いいね」や「コメント」が付くたびに、あなたに +1 点が入ります。',
    'スポットライトは重なった数だけタイムラインでの表示優先度が上がります。',
    'セルフスポット（Floor8より開放）：自分の投稿を1件だけ優先表示できます。こちらもいつでも好きな投稿に切り替え可能です。',
    'セルフスポット中の投稿に反応が付くと、さらに +1 点（通常の他者へのスポットライトとは別枠で利用可能）です。',
    'その日に投稿がない場合、期限切れ時に -10 点が反映されます。',
    '制限時間はダッシュボード上部に表示されます。',
];

export function FloorChangeModal({ isOpen, onClose, theme, fromFloor, toFloor, mode }: FloorChangeModalProps) {
    if (!isOpen) return null;

    const resolvedMode = mode ?? (toFloor > fromFloor ? 'promotion' : 'demotion');
    const isPromotion = resolvedMode === 'promotion';
    const isWelcome = resolvedMode === 'welcome';
    const title = isWelcome ? 'ようこそ' : isPromotion ? '昇格しました' : '降格しました';
    const subtitle = isWelcome
        ? 'Maison Portalでの新しい生活が始まります。'
        : isPromotion
            ? '新しいフロアの権限が解放されました。'
            : '現在のフロアに合わせて権限が調整されました。';
    const headline = isWelcome
        ? 'ようこそ、Maison Portalへ！'
        : isPromotion
            ? 'おめでとうございます！高層階への昇格です！'
            : '残念！低層階への降格です！';
    const showPollGuide = toFloor >= 9 || fromFloor >= 9;
    const pollGuide = [
        '投票（アンケート）機能はFloor9からご利用いただけます。',
        '1日3件まで作成できます。回答者にも1ポイント、投稿者にも1ポイントが入ります。',
        '回答は1つのアンケートにつき1回のみで、取り消しはできません。',
        '投票前に確認モーダルが表示されますので、内容をよくご確認ください。',
        '公開範囲は「フレンドのみ / 全体 / 指定フレンドのみ」から選べます。',
        '指定フレンドのみの場合は、登録済みの隣人から選択します。',
        '質問文と選択肢は投稿と同じ検閲を通過する必要があります。',
        '選択肢は最大20個まで設定できます。',
    ];

    return (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200 max-h-[85vh] flex flex-col">
                <div
                    className="px-6 py-4 flex justify-between items-center"
                    style={{ backgroundColor: theme.colors.primary, color: '#fff' }}
                >
                    <div>
                        <h3 className="text-lg font-bold tracking-widest">{title}</h3>
                        <div className="text-xs opacity-90">{subtitle}</div>
                    </div>
                    <button onClick={onClose} className="text-white/80 hover:text-white text-2xl">&times;</button>
                </div>

                <div className="p-6 space-y-5 text-sm overflow-y-auto flex-1">
                    <div className="text-xl font-bold text-center text-gray-900">{headline}</div>
                    <div>
                        <div className="font-semibold mb-1 text-gray-900">現在のフロア</div>
                        {isWelcome ? (
                            <div className="text-gray-700">
                                <span className="font-bold">Floor {toFloor} 「{theme.name}」</span>
                            </div>
                        ) : (
                            <div className="text-gray-700">
                                Floor {fromFloor} → <span className="font-bold">Floor {toFloor} 「{theme.name}」</span>
                            </div>
                        )}
                    </div>

                    {theme.features && (
                        <div className="space-y-3">
                            <div className="flex gap-3 rounded-md bg-green-50/80 p-3 border border-green-200">
                                <div className="text-lg">🔓</div>
                                <div>
                                    <div className="font-bold text-green-800">現在使える機能</div>
                                    <div className="text-green-900 leading-snug">{theme.features.current}</div>
                                </div>
                            </div>
                            <div className="flex gap-3 rounded-md bg-blue-50/80 p-3 border border-blue-200">
                                <div className="text-lg">🔮</div>
                                <div>
                                    <div className="font-bold text-blue-800">次のフロアで解放される機能</div>
                                    <div className="text-blue-900 leading-snug">{theme.features.next}</div>
                                </div>
                            </div>
                            <div className="flex gap-3 rounded-md bg-red-50/80 p-3 border border-red-200">
                                <div className="text-lg">⚠️</div>
                                <div>
                                    <div className="font-bold text-red-800">降格した場合の注意点</div>
                                    <div className="text-red-900 leading-snug">{theme.features.demotion}</div>
                                </div>
                            </div>
                            {theme.features.details && (
                                <div className="bg-gray-50 rounded-md p-3 border border-gray-200">
                                    <div className="font-bold mb-1 text-gray-900">このフロアで使える機能の詳細</div>
                                    <div className="whitespace-pre-line leading-relaxed text-gray-700">{theme.features.details}</div>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="bg-[#f8f6f0] rounded-md p-4 border border-[#e7dcc4] space-y-2">
                        <div className="font-bold text-gray-900">TokuScore の増減ルール</div>
                        <div className="text-xs text-gray-600">
                            投稿や交流で少しずつ増えていき、一定のスコアに達すると昇格します。投稿がない日は減点が入ります。
                        </div>
                        <ul className="list-disc pl-5 space-y-1 text-gray-700">
                            {POINTS_GUIDE.map((item) => (
                                <li key={item}>{item}</li>
                            ))}
                        </ul>
                        <div className="text-xs text-gray-600 mt-2">
                            自分の投稿には「いいね」「コメント」はできません。コメントは各投稿1回までです。
                        </div>
                    </div>
                    {showPollGuide && (
                        <div className="bg-white rounded-md p-4 border border-gray-200 space-y-2">
                            <div className="font-bold text-gray-900">投票（アンケート）機能について</div>
                            <ul className="list-disc pl-5 space-y-1 text-gray-700">
                                {pollGuide.map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
                <div className="px-6 pb-6 pt-2 flex justify-end">
                    <Button
                        onClick={onClose}
                        className="px-8"
                        style={{ backgroundColor: theme.colors.primary, color: '#fff' }}
                    >
                        続ける
                    </Button>
                </div>
            </div>
        </div>
    );
}
